import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can, ROLE_AR, TYPE_AR, APPROVAL_AR, APPROVAL_TONE } from "@/v2/lib/rbac";
import { useDebounced, errText } from "@/v2/lib/hooks";
import { GlassCard, Chip, Skeleton, Modal, Table, Bar, Pagination, AV_FALLBACK, PageHead } from "@/v2/admin/ui";
import { levelInfo } from "@/v2/lib/gamification";
import {
  Users, Search, Award, Eye, Power, ShieldCheck, Megaphone, Send, Building2, Sparkles, RotateCcw,
  KeyRound, Trash2, AlertTriangle,
} from "lucide-react";

function AnnounceModal({ departments, onClose }) {
  const [f, setF] = useState({ title: "", body: "", department: "" });
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!f.title.trim() || !f.body.trim()) return toast.error("العنوان والنص مطلوبان");
    setSending(true);
    try {
      const { data } = await api.post("/admin/announcements", f);
      toast.success(`تم إرسال الإعلان إلى ${data.sent} موظفًا`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر الإرسال"); } finally { setSending(false); }
  };
  return (
    <Modal title="إرسال إعلان للموظفين" onClose={onClose} testId="announce-modal">
      <div className="space-y-3">
        <div><label className="v2-label">العنوان</label><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="announce-title" className="v2-input" /></div>
        <div><label className="v2-label">النص</label><textarea rows={3} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="announce-body" className="v2-input resize-none" /></div>
        <div><label className="v2-label">الإدارة (اتركه فارغًا للجميع)</label>
          <select value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} className="v2-input">
            <option value="">كل الموظفين</option>
            {departments.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>
      <button onClick={send} disabled={sending} data-testid="announce-send" className="v2-btn w-full mt-5"><Send size={16} /> {sending ? "جارٍ الإرسال..." : "إرسال"}</button>
    </Modal>
  );
}

function EmployeeDrawer({ uid, departments = [], onClose }) {
  const { user: admin } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["adm-user", uid], queryFn: async () => (await api.get(`/admin/users/${uid}`)).data });
  const [role, setRole] = useState("");
  const [dept, setDept] = useState("");
  const [etype, setEtype] = useState("");
  const [resetting, setResetting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const patch = async (body, msg) => {
    try {
      await api.patch(`/admin/users/${uid}`, body);
      toast.success(msg);
      qc.invalidateQueries({ queryKey: ["adm-user", uid] });
      qc.invalidateQueries({ queryKey: ["adm-users"] });
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر التنفيذ"); }
  };

  const resetPassword = async () => {
    setResetting(true);
    try {
      const { data } = await api.post(`/admin/users/${uid}/reset-password`);
      toast.success(`تم تعيين كلمة المرور الجديدة: ${data.password}`, { duration: 6000 });
    } catch (e) { toast.error(errText(e)); } finally { setResetting(false); }
  };

  const removeUser = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success("تم حذف الحساب نهائيًا");
      qc.invalidateQueries({ queryKey: ["adm-users"] });
      setConfirmDelete(false);
      onClose();
    } catch (e) { toast.error(errText(e)); setDeleting(false); }
  };

  if (isLoading || !data) return <Modal title="ملف الموظف" onClose={onClose} wide testId="emp-drawer"><Skeleton className="h-64 w-full" /></Modal>;
  const u = data.user;
  const completed = u.completed_challenge_ids?.length || 0;
  const pct = Math.round((completed / (data.total_challenges || 20)) * 100);
  const lvl = levelInfo(u.points || 0);

  return (
    <Modal title="الملف الكامل للموظف" onClose={onClose} wide testId="emp-drawer">
      <div className="flex items-center gap-4 mb-5">
        <img src={u.avatar} alt={u.name} onError={AV_FALLBACK} className="w-16 h-16 rounded-full object-cover border-2 border-[#bb9661]/50" />
        <div className="flex-1">
          <div className="font-extrabold text-lg">{u.name}</div>
          <div className="text-sm text-[#bcc8cf]">{u.job_title} — {u.department} — {u.employee_id} — {u.email}</div>
          <div className="flex gap-2 mt-1.5 flex-wrap">
            <Chip tone="gold">{ROLE_AR[u.role] || u.role}</Chip>
            <Chip tone={u.active !== false ? "emerald" : "red"}>{u.active !== false ? "نشط" : "موقوف"}</Chip>
            <Chip tone={APPROVAL_TONE[u.approval_status || "approved"]}>{APPROVAL_AR[u.approval_status || "approved"]}</Chip>
            {u.employee_type && <Chip tone="muted">{TYPE_AR[u.employee_type]}</Chip>}
            <Chip tone="muted">الترتيب #{data.rank || "—"}</Chip>
            <Chip tone="emerald">{lvl.current.name}</Chip>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-5">
        {[["النقاط", u.points || 0], ["XP", lvl.xp], ["مكتمل", completed], ["سلسلة الحضور", u.streak || 0],
          ["الشارات", u.badges?.length || 0], ["المحاولات", data.attempts.length]].map(([l, v]) => (
          <div key={l} className="v2-inset p-3 text-center">
            <div className="text-lg font-extrabold v2-gold-text">{v}</div>
            <div className="text-[10px] text-[#bcc8cf]">{l}</div>
          </div>
        ))}
      </div>
      <div className="mb-5">
        <div className="flex justify-between text-xs text-[#bcc8cf] mb-1"><span>نسبة إكمال البرنامج</span><span>{pct}%</span></div>
        <Bar value={pct} />
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-5">
        <div>
          <h4 className="font-bold text-sm mb-2">التحديات</h4>
          <div className="v2-inset p-3 max-h-44 overflow-y-auto space-y-1.5" data-testid="emp-progress">
            {data.progress.map((p) => (
              <div key={p.id} className="flex justify-between text-xs">
                <span className="text-[#bcc8cf] truncate max-w-[170px]">اليوم {p.day} — {p.title}</span>
                <Chip tone={p.state === "completed" ? "emerald" : p.state === "in_progress" ? "gold" : "muted"}>
                  {p.state === "completed" ? "مكتمل" : p.state === "in_progress" ? "جارٍ" : "لم يبدأ"}
                </Chip>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="font-bold text-sm mb-2">نتائج الاختبارات والمحاولات</h4>
          <div className="v2-inset p-3 max-h-44 overflow-y-auto space-y-1.5" data-testid="emp-attempts">
            {data.attempts.length === 0 ? <p className="text-xs text-[#bcc8cf]">لا توجد محاولات.</p> : data.attempts.map((a) => (
              <div key={a.id} className="flex justify-between text-xs">
                <span className="text-[#bcc8cf] truncate max-w-[150px]">{a.challenge_title}</span>
                <span className="font-bold" style={{ color: a.passed ? "#91cab8" : "#F7A08A" }}>{a.score}% · {a.at?.slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="font-bold text-sm mb-2">سجل النقاط والنشاط</h4>
          <div className="v2-inset p-3 max-h-40 overflow-y-auto space-y-1.5">
            {data.transactions.length === 0 ? <p className="text-xs text-[#bcc8cf]">لا يوجد نشاط.</p> : data.transactions.map((t) => (
              <div key={t.id} className="flex justify-between text-xs">
                <span className="text-[#bcc8cf] truncate max-w-[170px]">{t.reason || t.type}</span>
                <span className={t.points >= 0 ? "text-[#91cab8] font-bold" : "text-[#F7A08A] font-bold"}>{t.points > 0 ? "+" : ""}{t.points}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="font-bold text-sm mb-2">سجل تسجيل الدخول</h4>
          <div className="v2-inset p-3 max-h-40 overflow-y-auto space-y-1" data-testid="emp-logins">
            {data.logins.length === 0 ? <p className="text-xs text-[#bcc8cf]">لا يوجد سجل دخول.</p> : data.logins.map((l) => (
              <div key={l.id} className="text-xs text-[#bcc8cf]" dir="ltr">{(l.at || "").slice(0, 16).replace("T", " ")}</div>
            ))}
          </div>
        </div>
      </div>

      {(u.badges || []).length > 0 && (
        <div className="mb-5">
          <h4 className="font-bold text-sm mb-2">الشارات والإنجازات</h4>
          <div className="flex gap-2 flex-wrap">{(u.badges_named || u.badges).map((b) => <Chip key={b} tone="gold"><Award size={12} /> {b}</Chip>)}</div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-white/10">
        {can(admin, "manage_users") && (
          <button onClick={() => patch({ active: u.active === false }, u.active === false ? "تم تفعيل الحساب" : "تم إيقاف الحساب")}
            data-testid="emp-toggle-active" className={u.active !== false ? "v2-btn-danger" : "v2-btn v2-btn-sm"}>
            <Power size={15} /> {u.active !== false ? "إيقاف الحساب" : "تفعيل الحساب"}
          </button>
        )}
        {can(admin, "manage_users") && (
          <div className="flex items-center gap-2">
            <Building2 size={16} className="text-[#bcc8cf]" />
            <select value={dept || u.department || ""} onChange={(e) => setDept(e.target.value)} data-testid="emp-dept-assign"
              className="v2-input !w-auto !py-2 text-xs font-bold">
              {!u.department && <option value="">— اختر الإدارة —</option>}
              {departments.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            {dept && dept !== u.department && (
              <button onClick={() => { patch({ department: dept }, "تم تحديث إدارة الموظف"); setDept(""); }}
                data-testid="emp-dept-save" className="v2-btn v2-btn-sm">حفظ الإدارة</button>
            )}
          </div>
        )}
        {can(admin, "manage_users") && (
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[#bcc8cf]" />
            <select value={etype || u.employee_type || ""} onChange={(e) => setEtype(e.target.value)} data-testid="emp-type"
              className="v2-input !w-auto !py-2 text-xs font-bold">
              <option value="">— نوع المسار —</option>
              {Object.entries(TYPE_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {etype && (
              <button onClick={() => { patch({ employee_type: etype }, "تم تحديث نوع المسار"); setEtype(""); }}
                data-testid="emp-type-save" className="v2-btn v2-btn-sm">حفظ النوع</button>
            )}
          </div>
        )}
        {can(admin, "manage_users") && (
          <button onClick={async () => {
            try {
              await api.post(`/admin/users/${uid}/reset-onboarding`);
              toast.success("سيُطلب من الموظف إكمال الإعداد الأولي عند الدخول القادم");
              qc.invalidateQueries({ queryKey: ["adm-user", uid] });
            } catch (e) { toast.error(errText(e)); }
          }} data-testid="emp-reset-onboarding" className="v2-btn-ghost v2-btn-sm">
            <RotateCcw size={14} /> إعادة ضبط الإعداد الأولي
          </button>
        )}
        {can(admin, "manage_users") && (
          <button onClick={resetPassword} disabled={resetting} data-testid="emp-reset-password"
            className="v2-btn-ghost v2-btn-sm">
            <KeyRound size={14} /> {resetting ? "جارٍ..." : "إعادة تعيين كلمة المرور (123456)"}
          </button>
        )}
        {can(admin, "manage_users") && u.role !== "super_admin" && admin?.id !== uid && (
          <button onClick={() => setConfirmDelete(true)} data-testid="emp-delete"
            className="v2-btn-danger v2-btn-sm">
            <Trash2 size={14} /> حذف الحساب نهائيًا
          </button>
        )}
        {admin?.role === "super_admin" && (
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-[#bcc8cf]" />
            <select value={role || u.role} onChange={(e) => setRole(e.target.value)} data-testid="emp-role" className="v2-input !w-auto !py-2 text-xs font-bold">
              {Object.entries(ROLE_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {role && role !== u.role && (
              <button onClick={() => { patch({ role }, "تم تغيير الدور"); setRole(""); }} data-testid="emp-role-save" className="v2-btn v2-btn-sm">حفظ الدور</button>
            )}
          </div>
        )}
        {role === "dept_manager" && !u.department && (
          <p className="text-xs text-[#F7A08A] w-full">حدّد إدارة الموظف واحفظها أولًا — تعيين «مدير إدارة» يتطلب إدارة محددة.</p>
        )}
      </div>

      {confirmDelete && (
        <Modal title="تأكيد الحذف النهائي" onClose={() => !deleting && setConfirmDelete(false)} testId="emp-delete-confirm">
          <div className="space-y-4">
            <div className="flex items-start gap-3 v2-inset p-4 rounded-xl border border-[#F7A08A]/30">
              <AlertTriangle size={22} className="text-[#F7A08A] shrink-0 mt-0.5" />
              <div className="text-sm leading-6">
                سيتم حذف حساب <b>{u.name}</b> ({u.email}) نهائيًا مع كل بياناته
                (النقاط، التقدّم، المحاولات، الإشعارات، سجل الدخول). <b>لا يمكن التراجع عن هذا الإجراء.</b>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={removeUser} disabled={deleting} data-testid="emp-delete-confirm-btn" className="v2-btn-danger flex-1">
                <Trash2 size={16} /> {deleting ? "جارٍ الحذف..." : "نعم، احذف نهائيًا"}
              </button>
              <button onClick={() => setConfirmDelete(false)} disabled={deleting} className="v2-btn-ghost flex-1">تراجع</button>
            </div>
          </div>
        </Modal>
      )}
    </Modal>
  );
}

export default function Employees() {
  const { user: admin } = useAuth();
  const [search, setSearch] = useState("");
  const [dept, setDept] = useState("");
  const [status, setStatus] = useState("");
  const [role, setRole] = useState("employee");
  const [page, setPage] = useState(1);
  const [etypeF, setEtypeF] = useState("");
  const [approvalF, setApprovalF] = useState("");
  const [selected, setSelected] = useState(null);
  const [announce, setAnnounce] = useState(false);
  const dSearch = useDebounced(search, 400);

  const { data, isLoading } = useQuery({
    queryKey: ["adm-users", dSearch, dept, status, role, etypeF, approvalF, page],
    queryFn: async () => (await api.get(`/admin/users?search=${encodeURIComponent(dSearch)}&department=${encodeURIComponent(dept)}&status=${status}&role=${role}&employee_type=${etypeF}&approval=${approvalF}&page=${page}&limit=15`)).data,
  });
  const rows = data?.rows || [];

  return (
    <div className="space-y-5" data-testid="admin-employees">
      <PageHead icon={Users} title="إدارة الموظفين" sub="بحث وفلترة ومتابعة كاملة لكل موظف.">
        {can(admin, "manage_users") && (
          <button onClick={() => setAnnounce(true)} data-testid="open-announce" className="v2-btn v2-btn-sm"><Megaphone size={15} /> إرسال إعلان</button>
        )}
      </PageHead>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute top-1/2 -translate-y-1/2 right-4 text-[#bcc8cf]" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="emp-search"
            placeholder="ابحث بالاسم أو البريد أو الرقم الوظيفي..." className="v2-input !pe-11" style={{ paddingInlineStart: "2.75rem" }} />
        </div>
        <select value={dept} onChange={(e) => { setDept(e.target.value); setPage(1); }} data-testid="emp-dept" className="v2-input !w-auto text-sm font-bold">
          <option value="">كل الإدارات</option>
          {(data?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={role} onChange={(e) => { setRole(e.target.value); setPage(1); }} data-testid="emp-role-filter" className="v2-input !w-auto text-sm font-bold">
          <option value="">كل الأدوار</option>
          {Object.entries(ROLE_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} data-testid="emp-status" className="v2-input !w-auto text-sm font-bold">
          <option value="">كل الحالات</option><option value="active">نشط</option><option value="inactive">موقوف</option>
        </select>
        <select value={etypeF} onChange={(e) => { setEtypeF(e.target.value); setPage(1); }} data-testid="emp-type-filter" className="v2-input !w-auto text-sm font-bold">
          <option value="">كل أنواع المسار</option>
          {Object.entries(TYPE_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={approvalF} onChange={(e) => { setApprovalF(e.target.value); setPage(1); }} data-testid="emp-approval-filter" className="v2-input !w-auto text-sm font-bold">
          <option value="">كل حالات الاعتماد</option>
          {Object.entries(APPROVAL_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {isLoading ? <Skeleton className="h-72 w-full" /> : rows.length === 0 ? (
        <GlassCard className="p-10 text-center text-[#bcc8cf] text-sm">لا توجد نتائج مطابقة.</GlassCard>
      ) : (
        <Table head={["الموظف", "الإدارة", "الدور", "النوع", "الاعتماد", "النقاط", "مكتمل", "الحالة", ""]} min={980} testId="emp-table">
          {rows.map((e) => (
            <tr key={e.id} data-testid="emp-row">
              <td>
                <div className="flex items-center gap-3">
                  <img src={e.avatar} alt={e.name} onError={AV_FALLBACK} className="w-9 h-9 rounded-full object-cover" />
                  <div><div className="font-semibold">{e.name}</div><div className="text-[10px] text-[#bcc8cf]">{e.employee_id} · {e.email}</div></div>
                </div>
              </td>
              <td className="text-[#bcc8cf]">{e.department}</td>
              <td><Chip tone={e.role === "employee" ? "muted" : "gold"}>{ROLE_AR[e.role] || e.role}</Chip></td>
              <td>{e.employee_type ? <Chip tone="muted">{TYPE_AR[e.employee_type]}</Chip> : <span className="text-[#bcc8cf]">—</span>}</td>
              <td><Chip tone={APPROVAL_TONE[e.approval_status || "approved"]}>{APPROVAL_AR[e.approval_status || "approved"]}</Chip></td>
              <td className="font-extrabold v2-gold-text">{e.points}</td>
              <td><span className="inline-flex items-center gap-1"><Award size={13} className="text-[#e3c799]" /> {e.completed_challenge_ids?.length || 0}</span></td>
              <td><Chip tone={e.active !== false ? "emerald" : "red"}>{e.active !== false ? "نشط" : "موقوف"}</Chip></td>
              <td><button onClick={() => setSelected(e.id)} data-testid={`emp-view-${e.employee_id}`} className="v2-btn-ghost v2-btn-sm"><Eye size={14} /> عرض</button></td>
            </tr>
          ))}
        </Table>
      )}

      <Pagination page={page} pages={data?.pages || 1} onChange={setPage} />

      {selected && <EmployeeDrawer uid={selected} departments={data?.departments || []} onClose={() => setSelected(null)} />}
      {announce && <AnnounceModal departments={data?.departments || []} onClose={() => setAnnounce(false)} />}
    </div>
  );
}
